import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { imgUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Eyebrow } from "@/components/Motion";

export default function Cart() {
  const { items, updateQty, remove, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <div data-testid="cart-page" className="pt-32 min-h-[70vh]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pb-24">
        <Eyebrow>Your Selection</Eyebrow>
        <h1 className="font-serif text-4xl md:text-6xl font-light mt-3 mb-14">Cart</h1>

        {items.length === 0 ? (
          <div data-testid="cart-empty">
            <p className="text-matte/50 font-serif text-2xl mb-8">Your cart is quietly empty.</p>
            <Link to="/shop" className="border border-matte px-8 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors duration-300">
              Browse the Shop
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-12 gap-16">
            <div className="md:col-span-8 space-y-8">
              {items.map(({ product, quantity }) => (
                <div key={product.id} className="flex gap-6 border-b border-matte/10 pb-8" data-testid={`cart-item-${product.id}`}>
                  <div className="w-28 h-32 bg-cream overflow-hidden flex-shrink-0">
                    <img src={imgUrl(product.images?.[0])} alt={product.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div className="flex justify-between gap-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide-sm text-plum mb-1">{product.category}</p>
                        <Link to={`/shop/${product.id}`} className="font-serif text-2xl link-underline">{product.title}</Link>
                      </div>
                      <button onClick={() => remove(product.id)} className="text-matte/40 hover:text-matte h-fit" data-testid={`cart-remove-${product.id}`}><X size={18} /></button>
                    </div>
                    <div className="flex justify-between items-end">
                      <div className="flex items-center border border-matte/20">
                        <button onClick={() => updateQty(product.id, quantity - 1)} className="p-2.5 hover:bg-cream transition-colors" data-testid={`cart-dec-${product.id}`}><Minus size={13} /></button>
                        <span className="w-10 text-center text-sm">{quantity}</span>
                        <button onClick={() => updateQty(product.id, quantity + 1)} className="p-2.5 hover:bg-cream transition-colors" data-testid={`cart-inc-${product.id}`}><Plus size={13} /></button>
                      </div>
                      <span className="font-serif text-xl">{money(product.price * quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="md:col-span-4">
              <div className="bg-cream p-8 sticky top-28">
                <Eyebrow>Summary</Eyebrow>
                <div className="mt-6 space-y-3 text-sm text-matte/70">
                  <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
                  <div className="flex justify-between text-matte/40"><span>Shipping</span><span>Calculated at checkout</span></div>
                </div>
                <div className="flex justify-between font-serif text-2xl mt-6 pt-6 border-t border-matte/15">
                  <span>Total</span><span>{money(subtotal)}</span>
                </div>
                <button
                  onClick={() => navigate("/checkout")}
                  data-testid="proceed-checkout-btn"
                  className="w-full mt-8 bg-matte text-ivory py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
